// TODO: Rework this file

import { useRef, useState } from 'react'
import { useMouse } from 'react-use'
import {
  ChartRacePoliticalPartiesResponse,
  ChartRaceResponse,
  ChartResponse,
  ChartTypeEnum,
} from '../../../../models/chart'
import CompletedIcon from './completed-icon'
import InProgressIcon from './in-progress-icon'
import MaximizeIcon from './maximize-icon'
import MinimizeIcon from './minimize-icon'
import NotStartedIcon from './not-started-icon'

type BottomTrackerProps = {
  currentMarksCount: number
  totalMarksCount: number
  chart?: ChartResponse
}

type RaceRange = {
  start: number
  end: number
}

type RaceDisplayModel = {
  name: string
  order: number
  range: RaceRange
  marksNeeded: number
  marksMade: number
  writeIns: number
  isRace: boolean
  isPartisan: boolean
  isProposal: boolean
  parties: ChartRacePoliticalPartiesResponse[]
}

const BottomProgressTracker = (props: BottomTrackerProps) => {
  const [collapsed, setCollapsed] = useState(false)
  const races: RaceDisplayModel[] = []
  let hasPartisan = false
  let maxPartisan = 0
  let partisanStarted = false
  let partisanCompleted = false
  let hasNonPartisan = false
  let nonPartisanStarted = false
  let maxNonPartisan = 0
  let nonPartisanCompleted = false
  let hasProposals = false
  let maxProposals = 0
  let proposalsStarted = false
  let proposalsCompleted = false
  const ref = useRef(null)
  const { docX, docY } = useMouse(ref)

  const getRaceCount = (race: ChartRaceResponse): number => {
    let raceNeeds = race.writeIns
    if (race.politicalParties && race.politicalParties.length > 0) {
      if (
        props.chart?.electionType.id === ChartTypeEnum.PARTISAN_PRIMARY &&
        race.name !== 'Preferred Party'
      ) {
        raceNeeds += race.candidates
      } else {
        raceNeeds += race.politicalParties
          .map((c) => c.candidates)
          .reduce((ca, cb) => ca + cb, 0)
      }
    } else {
      raceNeeds += race.candidates
      if (race.type.toLowerCase() === 'proposal') {
        raceNeeds += 2
      }
    }
    return raceNeeds
  }

  if (props.chart && props.chart.races && props.chart.races.length > 0) {
    const sortedRaces = props.chart.races.sort((ra, rb) =>
      ra.order < rb.order ? 1 : 0
    )
    let trackedCountNeeded = 0
    sortedRaces.forEach((race) => {
      const raceNeeds = getRaceCount(race)
      const raceModel: RaceDisplayModel = {
        name: race.name,
        order: race.order,
        parties: race.politicalParties,
        range: {
          start: trackedCountNeeded + 1,
          end: trackedCountNeeded + 1 + raceNeeds,
        },
        marksNeeded: raceNeeds,
        marksMade: 0,
        writeIns: race.writeIns,
        isRace: race.type.toLowerCase() === 'race',
        isPartisan:
          race.type.toLowerCase() === 'race' &&
          race.raceType.name.toLowerCase() === 'partisan',
        isProposal: race.type.toLowerCase() === 'proposal',
      }

      trackedCountNeeded += raceModel.marksNeeded

      if (
        props.currentMarksCount >= raceModel.range.start &&
        props.currentMarksCount < raceModel.range.end
      ) {
        raceModel.marksMade =
          props.currentMarksCount - raceModel.range.start + 1
      } else if (props.currentMarksCount >= raceModel.range.end) {
        raceModel.marksMade = raceModel.marksNeeded
      }
      races.push(raceModel)
      if (raceModel.isRace && raceModel.isPartisan) {
        hasPartisan = true
        if (raceModel.marksMade > 0) {
          partisanStarted = true
        }
        if (raceModel.range.end > maxPartisan) {
          maxPartisan = raceModel.range.end - 1
        }
        if (props.currentMarksCount >= maxPartisan) {
          partisanCompleted = true
        } else {
          partisanCompleted = false
        }
      } else if (raceModel.isRace && !raceModel.isPartisan) {
        hasNonPartisan = true
        if (raceModel.marksMade > 0) {
          nonPartisanStarted = true
        }
        if (raceModel.range.end > maxNonPartisan) {
          maxNonPartisan = raceModel.range.end - 1
        }
        if (props.currentMarksCount >= maxNonPartisan) {
          nonPartisanCompleted = true
        } else {
          nonPartisanCompleted = false
        }
      } else if (raceModel.isProposal) {
        hasProposals = true
        if (raceModel.marksMade > 0) {
          proposalsStarted = true
        }
        if (raceModel.range.end > maxProposals) {
          maxProposals = raceModel.range.end - 1
        }
        if (props.currentMarksCount >= maxProposals) {
          proposalsCompleted = true
        } else {
          proposalsCompleted = false
        }
      }
    })
  }

  const filteredRaces: RaceDisplayModel[] = races.filter(
    (r) =>
      (hasPartisan && !partisanCompleted && r.isPartisan) ||
      (hasNonPartisan &&
        (!hasPartisan || partisanCompleted) &&
        !nonPartisanCompleted &&
        r.isRace &&
        !r.isPartisan) ||
      (hasProposals &&
        (!hasPartisan || partisanCompleted) &&
        (!hasNonPartisan || nonPartisanCompleted) &&
        !proposalsCompleted &&
        r.isProposal) ||
      (hasProposals &&
        (!hasPartisan || partisanCompleted) &&
        (!hasNonPartisan || nonPartisanCompleted) &&
        r.isProposal) ||
      (hasNonPartisan &&
        !hasProposals &&
        nonPartisanCompleted &&
        (!hasPartisan || partisanCompleted) &&
        r.isRace &&
        !r.isPartisan) ||
      (hasPartisan &&
        partisanCompleted &&
        !hasNonPartisan &&
        !hasProposals &&
        r.isPartisan)
  )

  const next = filteredRaces.find((race) => {
    return race.marksMade >= 0 && race.marksMade < race.marksNeeded
  })
  const parties = next?.parties
    .map((item) => {
      return Array(item?.candidates).fill(item?.name)
    })
    .flat(2)

  // @ts-ignore
  return (
    <>
      <div ref={ref} className="fixed bottom-0 left-0 right-0 z-20">
        {collapsed && (
          <>
            <div className="flex items-center justify-center w-full gap-1 p-6 uppercase border-t border-b min-h-10 border-primary-blue bg-secondary-lightblue">
              <div className="font-medium text-primary-blue">
                TOTAL COMPLETED
              </div>{' '}
              <div>
                {props.currentMarksCount} of {props.totalMarksCount}
              </div>
            </div>
            <div
              className="absolute flex items-center justify-center w-12 h-12 bg-gray-200 rounded-full cursor-pointer -top-6 right-10"
              onClick={() => setCollapsed(false)}
            >
              <MaximizeIcon className="w-6 h-6" />
            </div>
          </>
        )}
        {!collapsed && (
          <>
            <div className="flex items-center justify-start w-full gap-1 uppercase min-h-10 bg-secondary-lightblue">
              <div className="absolute z-20 flex flex-row items-center justify-center gap-3 pl-5 -top-16">
                {hasPartisan && (
                  <div
                    className={`flex flex-col items-center justify-start px-6 py-2 rounded-tl-md rounded-tr-md border-t border-l border-r ${
                      partisanStarted && !partisanCompleted
                        ? ' bg-light-gold border-gold text-gold'
                        : partisanCompleted
                        ? ' bg-light-green-complete border-green-complete text-green-complete'
                        : ' bg-gray-200 border-gray-400 text-gray-400'
                    }`}
                  >
                    <div className="font-medium">PARTISAN SECTION</div>
                    <div className="flex items-center justify-center gap-1">
                      {partisanStarted && !partisanCompleted && (
                        <div>(In Progress)</div>
                      )}
                      {partisanCompleted && <div>(Completed)</div>}
                      {!partisanStarted && !partisanCompleted && (
                        <div>(Not Started)</div>
                      )}
                    </div>
                  </div>
                )}
                {hasNonPartisan && (
                  <div
                    className={`flex flex-col items-center justify-start px-6 py-2 rounded-tl-md rounded-tr-md border-t border-l border-r ${
                      nonPartisanStarted && !nonPartisanCompleted
                        ? ' bg-light-gold border-gold text-gold'
                        : nonPartisanCompleted
                        ? ' bg-light-green-complete border-green-complete text-green-complete'
                        : ' bg-gray-200 border-gray-400 text-gray-400'
                    }`}
                  >
                    <div className="font-medium">NONPARTISAN SECTION</div>
                    <div className="flex items-center justify-center gap-1">
                      {nonPartisanStarted && !nonPartisanCompleted && (
                        <div>(In Progress)</div>
                      )}
                      {nonPartisanCompleted && <div>(Completed)</div>}
                      {!nonPartisanStarted && !nonPartisanCompleted && (
                        <div>(Not Started)</div>
                      )}
                    </div>
                  </div>
                )}
                {hasProposals && (
                  <div
                    className={`flex flex-col items-center justify-start px-6 py-2 rounded-tl-md rounded-tr-md border-t border-l border-r ${
                      proposalsStarted && !proposalsCompleted
                        ? ' bg-light-gold border-gold text-gold'
                        : proposalsCompleted
                        ? ' bg-light-green-complete border-green-complete text-green-complete'
                        : ' bg-gray-200 border-gray-400 text-gray-400'
                    }`}
                  >
                    <div className="font-medium">PROPOSAL SECTION</div>
                    <div className="flex items-center justify-center gap-1">
                      {proposalsStarted && !proposalsCompleted && (
                        <div>(In Progress)</div>
                      )}
                      {proposalsCompleted && <div>(Completed)</div>}
                      {!proposalsStarted && !proposalsCompleted && (
                        <div>(Not Started)</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div className="z-30 flex items-stretch justify-start w-full gap-1 bg-white border-t border-b border-primary-blue">
                <div className="flex flex-col items-center justify-center px-8 py-8 bg-secondary-lightblue">
                  <div className="font-medium uppercase text-primary-blue">
                    Total Marked
                  </div>
                  <div className="uppercase">
                    {props.currentMarksCount} of {props.totalMarksCount}
                  </div>
                </div>
                <div className="flex items-center flex-grow h-full overflow-x-auto justiyf-start">
                  {filteredRaces.map((race, idx) => {
                    return (
                      <div
                        key={`${idx}-${race.name}`}
                        className="flex flex-col items-center justify-center h-full gap-1 px-8 py-4 bg-white border-r border-l-secondary-lightblue"
                      >
                        {race.marksMade === 0 && (
                          <div className="flex items-center justify-center w-6 h-6 mb-2 bg-gray-200 rounded-full">
                            <NotStartedIcon className="w-4 h-4" />
                          </div>
                        )}
                        {race.marksMade > 0 &&
                          race.marksMade < race.marksNeeded && (
                            <div className="flex items-center justify-center w-6 h-6 mb-2 rounded-full bg-light-gold">
                              <InProgressIcon className="w-4 h-4" />
                            </div>
                          )}
                        {race.marksMade === race.marksNeeded && (
                          <div className="flex items-center justify-center w-6 h-6 mb-2 rounded-full bg-light-green-complete">
                            <CompletedIcon className="w-4 h-4" />
                          </div>
                        )}
                        <div className="font-medium uppercase">{race.name}</div>
                        <div className="text-gray-500 uppercase">
                          {race.marksMade} of {race.marksNeeded} COMPLETE
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
            <div
              className="absolute z-40 flex items-center justify-center w-12 h-12 bg-gray-200 rounded-full cursor-pointer -top-6 right-10"
              onClick={() => setCollapsed(true)}
            >
              <MinimizeIcon className="w-6 h-6" />
            </div>
          </>
        )}
      </div>
      <div
        style={{
          position: 'absolute',
          borderRadius: '4px',
          backgroundColor: '#659C34',
          color: 'white',
          padding: '10px',
          left: docX + 20 + 'px',
          top: docY + 'px',
        }}
      >
        {next ? <>{Tooltip({ next, parties })}</> : <span>Completed!</span>}
      </div>
    </>
  )
}

export default BottomProgressTracker

interface Props {
  next: RaceDisplayModel
  parties: string[] | undefined
}

export const Tooltip: React.FC<Props> = ({ next, parties }) => {
  const getPartyAbbreviation = (index: number) => {
    if (parties) {
      return next.parties.find(
        (p) => p.name.toLowerCase() === parties[index]?.toLowerCase()
      )?.abbv
    }
  }

  if (next.isPartisan && next.name === 'Straight Party') {
    return (
      <span className="flex justify-center">{`straight, ${
        next.parties[next.marksMade].abbv
      }`}</span>
    )
  }

  if (next.isPartisan && next.name === 'Preferred Party') {
    return (
      <span className="flex justify-center">{`preferred, ${
        next.parties[next.marksMade].abbv
      }`}</span>
    )
  }

  if (next.isPartisan) {
    const diffMarks = next.marksNeeded - next.marksMade

    if (diffMarks <= next.writeIns) {
      return (
        <span className="flex justify-center">{`wri ${
          next.marksMade + 1 - (next?.marksNeeded - next.writeIns)
        }`}</span>
      )
    } else if (parties?.length) {
      return (
        <span className="flex justify-center">{`can ${
          next.marksMade + 1
        }, ${getPartyAbbreviation(next.marksMade)}`}</span>
      )
    } else {
      return (
        <span className="flex justify-center">{`can ${next.marksMade + 1}, ${
          next.parties[0].abbv
        }`}</span>
      )
    }
  } else if (next.isProposal) {
    const proposalText = next.marksMade === 0 ? 'yes' : 'no'
    return (
      <span className="flex justify-center">{`prop, ${proposalText}`}</span>
    )
  } else {
    const diffMarks = next.marksNeeded - next.marksMade

    if (diffMarks <= next.writeIns) {
      return (
        <span className="flex justify-center">{`wri ${
          next.marksMade + 1 - diffMarks
        }`}</span>
      )
    } else {
      return (
        <span className="flex justify-center">{`can ${
          next.marksMade + 1
        }`}</span>
      )
    }
  }
}
